import { useState, useEffect } from 'react'
import { firestore } from '../../../../utils/firebase/firebase'
import { toast } from 'react-toastify'
import AdminPage from '../../../../components/page/AdminPage'
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import { Box, Chip, MenuItem, TextField } from '@mui/material'
import { useHistory } from 'react-router-dom'
import nFormatter from '../../../../utils/helpers'

function UsersToolbar({
  roleFilter,
  setRoleFilter,
  adminCount,
  superAdminCount,
}) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Box sx={{ flex: 1 }} />
      <TextField
        select
        variant='standard'
        size='small'
        value={roleFilter}
        onChange={(e) => setRoleFilter(e.target.value)}
        sx={{ minWidth: 150, mr: 2 }}
        inputProps={{ 'aria-label': 'Filter by role' }}
      >
        <MenuItem value='all'>All roles</MenuItem>
        <MenuItem value='admins'>Admins ({adminCount})</MenuItem>
        <MenuItem value='superadmins'>
          Superadmins ({superAdminCount})
        </MenuItem>
      </TextField>
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  )
}

function Users() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [roleFilter, setRoleFilter] = useState('all')
  const history = useHistory()

  const adminCount = users.filter((u) => u.isAdmin || u.isSuperAdmin).length
  const superAdminCount = users.filter((u) => u.isSuperAdmin).length
  const rows = users.filter((u) => {
    if (roleFilter === 'admins') return u.isAdmin || u.isSuperAdmin
    if (roleFilter === 'superadmins') return u.isSuperAdmin
    return true
  })

  useEffect(() => {
    try {
      const localUsers = JSON.parse(localStorage.getItem('users'))
      if (localUsers) {
        setUsers(localUsers)
        setLoading(false)
      }
    } catch (error) {
      console.log(error)
    }
    // Get user Data
    const userArray = []
    firestore
      .collection('users')
      .get()
      .then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          const id = doc.id
          userArray.push({ ...doc.data(), id: id })
        })
        setUsers(userArray)
        setLoading(false)
        try {
          localStorage.setItem('users', JSON.stringify(userArray))
        } catch (error) {
          console.log(error)
        }
      })
      .catch((error) => {
        toast.error(error)
      })
  }, [])

  const columns = [
    {
      field: 'firstName',
      headerName: 'First name',
      flex: 2,
    },
    {
      field: 'lastName',
      headerName: 'Last name',
      flex: 2,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 3,
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      valueGetter: ({ row }) => {
        if (row.isSuperAdmin === true) return 'Superadmin'
        if (row.isAdmin === true) return 'Admin'
        return ''
      },
      renderCell: (params) => {
        if (params.value === 'Superadmin') {
          return (
            <Chip
              label='Superadmin'
              color='primary'
              size='small'
              sx={{ p: 1 }}
            />
          )
        } else if (params.value === 'Admin') {
          return (
            <Chip
              label='Admin'
              color='primary'
              variant='outlined'
              size='small'
              sx={{ p: 1 }}
            />
          )
        }
        return <></>
      },
    },
    {
      field: 'mobile',
      headerName: 'Phone',
      flex: 2,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const status = params.value
        if (status === 'active') {
          return (
            <Chip label='Active' color='success' size='small' sx={{ p: 1 }} />
          )
        } else if (status === 'past_due' || status === 'unpaid') {
          return (
            <Chip label='Overdue' color='error' size='small' sx={{ p: 1 }} />
          )
        } else if (status === 'trialing') {
          return <Chip label='Trial' color='info' size='small' sx={{ p: 1 }} />
        } else {
          return status ? (
            <Chip
              label={status}
              size='small'
              sx={{ p: 1, textTransform: 'capitalize' }}
            />
          ) : (
            <></>
          )
        }
      },
    },
    {
      field: 'credits',
      headerName: 'Credits',
      flex: 1,
      renderCell: (params) => {
        return nFormatter(params.value, { digits: 1 })
      },
    },
  ]

  return (
    <AdminPage loading={loading}>
      <Box sx={{ width: '100%', height: 'calc(100vh - 150px)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          autoPageSize
          loading={loading}
          slots={{ toolbar: UsersToolbar }}
          slotProps={{
            toolbar: {
              roleFilter,
              setRoleFilter,
              adminCount,
              superAdminCount,
            },
          }}
          columnHeaderHeight={70}
          onRowClick={(row) => history.push(`/admin/users/${row.id}`)}
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: 'primary.main',
            '& .MuiDataGrid-row:hover': {
              color: 'primary.main',
            },
            '.MuiDataGrid-columnHeaders': {
              backgroundColor: 'primary.main',
              color: 'black',
              borderRadius: 0,
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '1rem',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
              },
              '.MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '.MuiDataGrid-sortIcon': {
                color: 'black',
              },
              '.MuiDataGrid-menuIconButton': { color: 'black' },
            },
          }}
        />
      </Box>
    </AdminPage>
  )
}

export default Users
